/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** What resending and copying an invitation leave behind on the row. */

/**
 * Whether the menu offers resend and copy-link at all.
 *
 * Only on a pending invitation, and only to somebody who could have sent it:
 * both mint a fresh token, so offering them to a reader who cannot invite
 * would hand out a working way in.
 *
 * @param pending - Whether the row is an invitation rather than a person.
 * @param canInvite - Whether the reader holds the invite permission.
 * @returns Whether to draw the two items.
 */
export const offersInviteActions = (pending: boolean, canInvite: boolean): boolean => (
  pending && canInvite
);

/**
 * Hands a link to the clipboard, reporting whether it landed.
 *
 * The fallback matters: the clipboard API needs a secure context and a
 * recent gesture, and when it refuses the copy silently does nothing — the
 * operator pastes whatever was there before and sends the wrong link.
 *
 * @param url - The invitation link.
 * @returns Whether the link is now on the clipboard.
 */
export async function copyToClipboard(url: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return legacyCopy(url);
  }
}

/**
 * The pre-clipboard-API copy, for when the modern one is unavailable.
 *
 * @param url - The invitation link.
 * @returns Whether the copy succeeded.
 */
function legacyCopy(url: string): boolean {
  try {
    const field = document.createElement('textarea');
    field.value = url;
    // Off-screen rather than hidden: a field with display:none cannot be
    // selected, and the copy would report success having copied nothing.
    field.style.position = 'fixed';
    field.style.top = '-1000px';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(field);
    return ok;
  } catch {
    return false;
  }
}
